import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router';
import { loginInput, type LoginInput } from '@barbeiropro/contracts';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/shared/field';
import { api, errorMessage } from '@/lib/api';
import { meKey } from '@/lib/session';
import { AuthLayout } from './auth-layout';
import { FormError } from './form-error';

export function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const qc = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const form = useForm<LoginInput>({ resolver: zodResolver(loginInput) });
  const { errors, isSubmitting } = form.formState;

  const onSubmit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      const r = await api.post<{ home?: '/plataforma' | '/app' }>('/auth/login', values);
      await qc.invalidateQueries({ queryKey: meKey });
      const next = params.get('next');
      // operador da plataforma (sem barbearia) vai direto para /plataforma; nunca para /app
      const area = r.home === '/plataforma' ? '/plataforma' : '/app';
      navigate(next && next.startsWith(area) ? next : area, { replace: true });
    } catch (e) {
      setError(errorMessage(e));
    }
  });

  return (
    <AuthLayout
      title="Entrar"
      subtitle="Acesse a agenda e o caixa da sua barbearia."
      footer={
        <>
          Ainda não usa o BarbeiroPro?{' '}
          <Link to="/cadastrar" className="font-medium text-primary underline-offset-4 hover:underline">
            Cadastre sua barbearia
          </Link>
        </>
      }
    >
      <form onSubmit={onSubmit} className="grid gap-4" noValidate>
        <FormError message={error} />
        <Field id="email" label="E-mail" error={errors.email?.message}>
          <Input id="email" type="email" autoComplete="email" aria-invalid={!!errors.email} {...form.register('email')} />
        </Field>
        <Field id="password" label="Senha" error={errors.password?.message}>
          <Input id="password" type="password" autoComplete="current-password" aria-invalid={!!errors.password} {...form.register('password')} />
        </Field>
        <Link to="/esqueci-senha" className="justify-self-end text-sm text-muted-foreground underline-offset-4 hover:underline">
          Esqueci minha senha
        </Link>
        <Button type="submit" size="lg" disabled={isSubmitting}>
          {isSubmitting ? 'Entrando…' : 'Entrar'}
        </Button>
      </form>
    </AuthLayout>
  );
}
